import { handleError } from '@anchorage/sentry';
import { type ReactNode, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  type FallbackProps,
  reportCaughtError,
  toFallbackProps,
} from './caughtError';
import { DefaultErrorFallback } from './DefaultErrorFallback';

export type QueryErrorBoundaryProps = {
  children: ReactNode;
  /** Apollo/async error — when set, shows fallback instead of children */
  error?: Error;
  /** Custom fallback renderer. Defaults to DefaultErrorFallback (AlertBanner). */
  fallbackRender?: (props: FallbackProps) => ReactNode;
  /** Override default title in the DefaultErrorFallback */
  title?: string;
  /** Called when the boundary resets (retry button or error prop clears) */
  onReset?: () => void;
};

export function QueryErrorBoundary({
  children,
  error,
  fallbackRender,
  title,
  onReset,
}: QueryErrorBoundaryProps) {
  // Report Apollo/async errors to Sentry (once per error instance)
  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  // Apollo/async error path: show fallback without needing a render throw
  if (error) {
    if (fallbackRender) {
      return (
        <>{fallbackRender({ error, resetErrorBoundary: () => onReset?.() })}</>
      );
    }
    return (
      <DefaultErrorFallback
        error={error}
        resetErrorBoundary={() => onReset?.()}
        title={title}
      />
    );
  }

  // Standard React error boundary path: catches render errors
  return (
    <ErrorBoundary
      onError={reportCaughtError}
      onReset={onReset}
      fallbackRender={(caught) => {
        const props = toFallbackProps(caught);
        return fallbackRender ? (
          fallbackRender(props)
        ) : (
          <DefaultErrorFallback
            error={props.error}
            resetErrorBoundary={props.resetErrorBoundary}
            title={title}
          />
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
